/**
 * Hero System · family adapters (PO 2026-02-11 · Reference Contract Adoption).
 *
 * The canonical `HeroData` contract (see `/app/memory/HERO_SYSTEM.md`)
 * is the future entry point for every Hero consumer. During the
 * reference-adoption phase, existing hero components keep their
 * legacy prop bags for backward compatibility — these adapters
 * translate a canonical `HeroData` object into the legacy shape each
 * family expects.
 *
 * This layer lets Voyage Home and Destination Guide build their
 * hero as a single `HeroData` object at the call site (the eventual
 * contract) while the actual `WorkspaceHero` / `EditorialHero`
 * components remain untouched, guaranteeing visual parity.
 */
import { warnOnHeroDataDrift } from "./validateHeroData";


/**
 * `toWorkspaceHeroProps(data)` — HeroData → WorkspaceHero prop bag.
 *
 * `WorkspaceHero` today accepts:
 *   shipName, voyageLabel, dateRange, countdown, contextLine, phase,
 *   primaryAction, heroImage, eyebrow, accentColor, testId.
 *
 * Mapping strategy:
 *   title      → shipName
 *   subtitle   → voyageLabel (splits on the first "·" if a date range
 *                 is embedded — legacy CruiseHome behaviour)
 *   meta.phase → phase
 *   meta.countdown → countdown
 *   meta.context_line → contextLine
 *   meta.date_range OR the part after "·" in subtitle → dateRange
 *   eyebrow    → eyebrow { label, color }
 *   action     → primaryAction { label, onClick, testId, icon }
 *   image/focal_point/tint/source_provenance → heroImage {
 *       image_url, focal_point, environmental_mood, source
 *     }
 */
export function toWorkspaceHeroProps(data) {
  warnOnHeroDataDrift(data, {
    consumer: "WorkspaceHero",
    familyExpected: "operational",
  });
  const d = data || {};
  const meta = d.meta || {};

  // Split subtitle "October 2026 · Oct 25 → Nov 1, 2026" into
  // (voyageLabel, dateRange) when a bullet is present.
  let voyageLabel = d.subtitle || null;
  let dateRange = meta.date_range || null;
  if (voyageLabel && !dateRange && voyageLabel.includes(" · ")) {
    const [head, ...rest] = voyageLabel.split(" · ");
    voyageLabel = head.trim();
    dateRange = rest.join(" · ").trim() || null;
  }

  return {
    shipName: d.title || null,
    voyageLabel,
    dateRange,
    countdown: meta.countdown
      ? { value: meta.countdown.value, unitLabel: meta.countdown.unit_label }
      : undefined,
    contextLine: meta.context_line || undefined,
    phase: meta.phase || undefined,
    primaryAction: d.action
      ? {
          label: d.action.label,
          onClick: d.action.on_click || d.action.onClick,
          testId: d.action.test_id || d.action.testId,
          icon: d.action.icon,
        }
      : undefined,
    heroImage: d.image
      ? {
          image_url: d.image,
          alt: d.alt,
          focal_point: d.focal_point,
          environmental_mood: d.tint || undefined,
          source: d.source_provenance || undefined,
          // Do NOT synthesise a quality tier — that decision belongs
          // to the media resolver. Passing an unresolved quality
          // triggers the `OFFICIAL PHOTOGRAPHY` chip inside
          // `CruiseHeroFrame`, which would be a visual regression
          // for consumers whose upstream data left quality unset.
          quality: d.image_quality || undefined,
        }
      : (d.image_source_type ? { image_url: null } : undefined),
    eyebrow: d.eyebrow || undefined,
    // Workspace-scoped accent — colours the ship-name headline so
    // each Operational surface has a distinct identity while the
    // architecture (frame, layout, motion, contract) stays shared.
    // See `memory/HERO_SYSTEM.md` · Canonical Hero Colour Matrix.
    accentColor: d.accent_color || undefined,
    // Optional accent-tinted background gradient — applied by
    // `WorkspaceHero` when a workspace has no ship photograph. Lets
    // Journey / Briefing / Route Map carry a distinct identity
    // gradient (like Cruises / Finance) without touching the
    // photograph-forward Voyage Home. See the Colour Matrix.
    baseGradient: d.base_gradient || undefined,
  };
}


/**
 * `toEditorialHeroProps(data)` — HeroData → EditorialHero prop bag.
 *
 * `EditorialHero` today accepts:
 *   image, alt, objectPosition, eyebrow { icon, label }, contextLabel,
 *   title, dek, action, testId.
 *
 * Mapping strategy:
 *   image           → image
 *   alt             → alt (falls back to title)
 *   focal_point     → objectPosition ("62% 55%")
 *   eyebrow         → eyebrow (label + icon passthrough)
 *   subtitle        → contextLabel
 *   title           → title
 *   meta.dek        → dek
 *   action          → action (rendered by caller)
 */
export function toEditorialHeroProps(data) {
  warnOnHeroDataDrift(data, {
    consumer: "EditorialHero",
    familyExpected: "editorial",
  });
  const d = data || {};
  const meta = d.meta || {};
  const focal = d.focal_point || {};
  const objectPosition = (typeof focal.x === "number" && typeof focal.y === "number")
    ? `${Math.round(focal.x * 100)}% ${Math.round(focal.y * 100)}%`
    : undefined;

  return {
    image: d.image || null,
    alt: d.alt || d.title || "",
    objectPosition,
    eyebrow: d.eyebrow || undefined,
    contextLabel: d.subtitle || undefined,
    title: d.title || null,
    dek: meta.dek || undefined,
    action: d.action || undefined,
  };
}
